import { DataError, type DataSource, type Entities, type EntityKey, type ListQuery, type Page } from './source';

/** Gọi một đường dẫn của worker `api` (Hub bọc token Office 365); lỗi HTTP ném Error có `status`. */
export type ApiFetch = (path: string) => Promise<unknown>;

type Params = Record<string, string | number | boolean | undefined>;

/** Cách một thực thể đọc qua `api`. Màn không biết chuyện này: chỉ gọi DataSource. */
export interface ApiRoute<T> {
  path: string;
  /** ListQuery → tham số của route (mặc định: q, offset, limit) */
  query?: (q: ListQuery) => Params;
  /** JSON của route → trang kết quả; `total` bỏ trống thì suy ra từ số dòng đã có */
  parse: (json: unknown) => { items: T[]; total?: number; hasMore?: boolean };
  /** lấy một bản ghi theo id (mặc định: `${path}/${id}`) */
  getPath?: (id: string) => string;
  parseOne?: (json: unknown) => T | null;
}

export type ApiRoutes = { [K in EntityKey]?: ApiRoute<Entities[K]> };

const qs = (p: Params) => {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
};

const defaultQuery = (q: ListQuery): Params => ({ q: q.q?.trim(), offset: q.offset, limit: q.limit });

function toDataError(e: unknown): DataError {
  if (e instanceof DataError) return e;
  // giữ nguyên nhân để soi trên prod (không có token / dữ liệu trong lỗi): 'network' gộp nhiều thứ — 5xx, hết 15s, token lỗi
  console.warn('[hub/api]', (e as { status?: number } | null)?.status ?? '-', e instanceof Error ? `${e.name}: ${e.message}` : String(e));
  const st = (e as { status?: number } | null)?.status;
  if (st === 401 || st === 403) return new DataError('noperm');
  if (st === 404) return new DataError('notfound');
  if (st === undefined || st >= 500 || e instanceof TypeError) return new DataError('network');
  return new DataError('unknown');
}

/** Nhà cung cấp dữ liệu đọc từ worker `api`; thực thể chưa khai route thì `notfound`. Dùng với `pickSource`. */
export function apiSource(call: ApiFetch, routes: ApiRoutes): DataSource {
  const route = <K extends EntityKey>(e: K) => {
    const r = routes[e] as ApiRoute<Entities[K]> | undefined;
    if (!r) throw new DataError('notfound', `Chưa có route api cho ${e}`);
    return r;
  };
  return {
    async list<K extends EntityKey>(entity: K, query: ListQuery = {}): Promise<Page<Entities[K]>> {
      const r = route(entity);
      try {
        const out = r.parse(await call(r.path + qs((r.query ?? defaultQuery)(query))));
        const total = out.total ?? (query.offset ?? 0) + out.items.length + (out.hasMore ? 1 : 0);
        return { items: out.items, total };
      } catch (e) {
        throw toDataError(e);
      }
    },
    async get<K extends EntityKey>(entity: K, id: string): Promise<Entities[K] | null> {
      const r = route(entity);
      try {
        const json = await call(r.getPath ? r.getPath(id) : `${r.path}/${encodeURIComponent(id)}`);
        return r.parseOne ? r.parseOne(json) : (json as Entities[K] | null);
      } catch (e) {
        const err = toDataError(e);
        if (err.code === 'notfound') return null;
        throw err;
      }
    },
  };
}

/** Chọn nguồn theo thực thể: có route `api` thì dùng `api`, còn lại dữ liệu mẫu. */
export function pickSource(api: DataSource, fixtures: DataSource, apiEntities: ReadonlySet<string>): DataSource {
  const of = (e: string) => (apiEntities.has(e) ? api : fixtures);
  return {
    list: (entity, query) => of(entity).list(entity, query),
    get: (entity, id) => of(entity).get(entity, id),
  };
}
